import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTypedSelector } from '../store';
import { selectUser } from '../store/userData/selectors';
import { sendAnalyticsEvent } from '../store/analytics/actionWrappers';
import { AnalyticsEvent } from '../store/analytics/stateType';
import { getAnalyticsSessionId } from '../store/app/utils';

const generateUserId = (email: string | undefined): string => {
  if (!email) return 'guest';

  const str = email.trim().toLowerCase();
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
};

export const useAnalytics = (eventType: string) => {
  const [timer, setTimer] = React.useState(0);
  const dispatch = useDispatch();
  const router = useLocation();
  const user = useTypedSelector(selectUser);

  const sessionId = useMemo(() => getAnalyticsSessionId() || 'no-session', []);

  const userId = useMemo(() => generateUserId(user?.email), [user?.email]);

  const sendEvent = useCallback(
    (data: Partial<AnalyticsEvent> = {}) => {
      dispatch(
        sendAnalyticsEvent({
          eventType,
          userId,
          sessionId,
          userCountries: user?.countries,
          userRole: user?.expansionUserRole,
          eventLocation: router.pathname,
          source: 'ETR2',
          ...data,
        }),
      );
    },
    [dispatch, eventType, userId, sessionId, user, router.pathname],
  );

  const actionStart = () => {
    setTimer(Date.now());
  };
  const actionDiscarded = () => {
    setTimer(0);
  };

  const actionEnd = (data: Partial<AnalyticsEvent> = {}) => {
    if (timer === 0) {
      actionDiscarded();
      return;
    }
    sendEvent({
      ...data,
      context: {
        ...data.context,
        duration: Math.floor((Date.now() - timer) / 1000),
      },
    });
    actionDiscarded();
  };

  return { sendEvent, actionEnd, actionStart, actionDiscarded, timer };
};
